<script lang="ts">
import { UnwrapRef, getCurrentInstance, onBeforeMount, ref, shallowRef, watch } from 'vue';
import { IFloatingFilterParams } from 'ag-grid-enterprise';
import { SelectValues } from '@/types/select';
import Wrapper from './FilterWrapper.vue';

interface FilterModel<T> {
  filter: UnwrapRef<T>;
  type: 'equals';
}

export interface LocalFilterModel<T> {
  filter: T | null;
  type: 'equals';
}
</script>

<script setup lang="ts" generic="T">
const localFilterModel = ref<LocalFilterModel<T>>({ filter: null, type: 'equals' });

const params = shallowRef<IFloatingFilterParams & { values: SelectValues<T> }>();

defineExpose({
  onParentModelChanged: updateLocalFilter,
});

onBeforeMount(() => {
  params.value = getCurrentInstance()?.proxy.$data.params;
  if (params.value === undefined) return;
  const filterModel = params.value.api.getColumnFilterModel<FilterModel<T>>(params.value.column);
  updateLocalFilter(filterModel);
});

function updateLocalFilter(filterModel: FilterModel<T> | null) {
  if (filterModel === null) {
    const firstValue = params.value?.values[0];
    localFilterModel.value.filter =
      firstValue === undefined ? null : (firstValue.value as UnwrapRef<T>);
  } else {
    localFilterModel.value = filterModel;
  }
}

watch(localFilterModel, applyFilter, { deep: true, immediate: true });

async function applyFilter() {
  if (params.value === undefined) return;
  await params.value.api.setColumnFilterModel<FilterModel<T>>(
    params.value.column,
    localFilterModel.value.filter === null ? null : (localFilterModel.value as FilterModel<T>)
  );
  params.value.api.onFilterChanged();
}
</script>

<template>
  <Wrapper :data-test-filter="params?.column.getColId()">
    <select
      v-model="localFilterModel.filter"
      class="ag-input-field-input ag-text-field-input"
      style="text-align: center"
    >
      <option v-for="{ value, text } in params?.values" :key="text" :value="value">
        {{ text }}
      </option>
    </select>
  </Wrapper>
</template>
